import type { AttendanceStatus } from "../types/kioskApiTypes";
import type { AbsenceRun } from "../utils/classWeekHelpers";
import { fmtHM, shortName, STATUS_BUTTONS } from "../utils/classWeekHelpers";

export function SummaryRow({
  label,
  status,
  runsByDay,
  activeDays,
  hideIfEmpty,
}: {
  label: string;
  status: AttendanceStatus;
  runsByDay: Map<number, AbsenceRun[]>;
  activeDays: number[];
  hideIfEmpty?: boolean;
}) {
  // Filter runs for this status across the active days only.
  const perDay: AbsenceRun[][] = activeDays.map((dow) =>
    (runsByDay.get(dow) ?? []).filter((r) => r.status === status),
  );
  const total = perDay.reduce((n, list) => n + list.length, 0);
  if (hideIfEmpty && total === 0) return null;

  const meta = STATUS_BUTTONS.find((b) => b.key === status);
  const summaryBadgeTone =
    status === "absent_unexcused"
      ? "bg-rose-100/70 text-rose-700 border border-rose-200/80"
      : meta?.activeTone ?? "";
  const filledCellTone =
    status === "absent_excused"
      ? "bg-sky-50/60"
      : status === "absent_unexcused"
      ? "bg-rose-50/55"
      : "";

  return (
    <tr className="border-b last:border-b-0 border-slate-200 bg-slate-50/60">
      <td className="px-2 py-2 text-center border-r border-slate-200 align-top">
        {meta ? (
          <span
            className={`inline-flex items-center justify-center px-1.5 h-5 rounded text-[10px] font-medium ${summaryBadgeTone}`}
            title={meta.title}
          >
            {label}
          </span>
        ) : (
          <span className="text-[10px] text-slate-500">{label}</span>
        )}
      </td>
      {perDay.map((runs, i) => (
        <td
          key={i}
          className={`border-r last:border-r-0 border-slate-200 align-top px-2 py-1.5 ${runs.length > 0 ? filledCellTone : ""}`}
        >
          {runs.length === 0 ? (
            <div className="h-3" />
          ) : (
            <ul className="flex flex-col gap-0.5">
              {runs.map((r) => (
                <li
                  key={r.student_id + r.start_at}
                  className="leading-tight flex items-baseline justify-between gap-1 text-slate-800"
                  title={r.notes ? `${r.name} — ${r.notes}` : r.name}
                >
                  <span className="truncate flex items-center gap-1 text-[12px]">
                    {shortName(r.name)}
                    {r.notes ? (
                      <span
                        className="text-slate-400 cursor-help"
                        aria-label={r.notes}
                      >
                        ⓘ
                      </span>
                    ) : null}
                  </span>
                  <span className="text-[9px] text-slate-500 whitespace-nowrap">
                    {r.matched_lessons >= r.total_lessons && r.total_lessons > 1
                      ? "verm. ganztägig"
                      : `${fmtHM(r.start_at)}–${fmtHM(r.end_at)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </td>
      ))}
    </tr>
  );
}
